import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { Device } from "@twilio/voice-sdk";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { startRinger, stopRinger } from "../utils/notificationSound";
import { initTelnyxClient, telnyxNewCall, destroyTelnyxClient } from "../utils/telnyxVoice";

const VoiceContext = createContext(null);

export const useVoice = () => {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error("useVoice must be used within VoiceProvider");
  return ctx;
};

/** Detect Android browser/WebView */
import { isAndroid, isInNativeWrapper, requestMicPermission, MIC_GRANT_CACHE_KEY } from "../utils/voicePermissions";
export { isAndroid, isInNativeWrapper, requestMicPermission, MIC_GRANT_CACHE_KEY };

export const VoiceProvider = ({ children }) => {
  const [device, setDevice] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callState, setCallState] = useState("idle");
  const [incomingCall, setIncomingCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callerId, setCallerId] = useState("");
  const [minutesUsed, setMinutesUsed] = useState(0);
  const [minutesLimit, setMinutesLimit] = useState(750);
  const [isReady, setIsReady] = useState(false);
  const [micPermission, setMicPermission] = useState(null); // null = unknown, true = granted, false = denied
  const timerRef = useRef(null);
  const callStartRef = useRef(null);
  const deviceRef = useRef(null);
  const registeredRef = useRef(false);
  const activeCallRef = useRef(null);
  const selectedCallerIdRef = useRef("");
  const callerIdsRef = useRef([]);
  const telnyxEnabledRef = useRef(false);
  const telnyxInitRef = useRef(false);
  const [availableCallerIds, setAvailableCallerIds] = useState([]);
  const [selectedCallerId, setSelectedCallerIdState] = useState("");
  const [heldCalls, setHeldCalls] = useState([]);

  const updateActiveCall = (call) => {
    activeCallRef.current = call;
    setActiveCall(call);
  };

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  /** Fetch a fresh voice token from the backend */
  const fetchToken = useCallback(async () => {
    const res = await axios.get(`${API}/voice/token`, getAuthConfig());
    const { token, caller_id, minutes_used, minutes_limit, available_caller_ids, telnyx_enabled } = res.data;
    setCallerId(caller_id);
    setMinutesUsed(minutes_used);
    setMinutesLimit(minutes_limit);
    const ids = available_caller_ids || [];
    setAvailableCallerIds(ids);
    callerIdsRef.current = ids;
    telnyxEnabledRef.current = !!telnyx_enabled;
    if (!selectedCallerIdRef.current) {
      let pick = "";
      try { pick = localStorage.getItem("voice_selected_caller_id") || ""; } catch { /* ignore */ }
      if (!pick || !ids.some((n) => n.phone_number === pick)) pick = caller_id;
      selectedCallerIdRef.current = pick;
      setSelectedCallerIdState(pick);
    }
    return token;
  }, []);

  /** Create and configure the Twilio Device (but don't register yet) */
  const createDevice = useCallback(async () => {
    try {
      const token = await fetchToken();

      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }

      const newDevice = new Device(token, {
        codecPreferences: ["opus", "pcmu"],
        enableRingingState: true,
        closeProtection: "A call is in progress. Leaving will end the call.",
        forceAggressiveIceNomination: true,
        // Edge location helps with Android connectivity
        edge: "ashburn",
      });

      // Custom ringer owns incoming-call audio (per-user/location ringtones)
      try { newDevice.audio.incoming(false); } catch { /* older SDK */ }

      newDevice.on("registered", () => {
        registeredRef.current = true;
        setIsReady(true);
      });

      newDevice.on("unregistered", () => {
        registeredRef.current = false;
        setIsReady(false);
      });

      newDevice.on("error", (err) => {
        console.error("Voice device error:", err.code, err.message);
        // Don't toast on common transient errors
        if (![31009, 31005].includes(err.code)) {
          if (err.code === 31208 || err.code === 31401) {
            toast.error("Microphone access denied. Please allow microphone in your browser settings.");
            setMicPermission(false);
          } else if (err.code === 31201) {
            toast.error("No microphone found. Please connect a microphone.");
          } else if (err.code === 31003) {
            toast.error("Network issue. Please check your connection and try again.");
          } else {
            toast.error("Voice connection error. Please try again.");
          }
        }
      });

      newDevice.on("incoming", (call) => {
        let custom = {};
        try { custom = call.customParameters ? Object.fromEntries(call.customParameters) : {}; } catch { /* ignore */ }

        // A resumed held call comes back as an incoming connection tagged
        // Resume=1 — answer it silently instead of ringing.
        if (custom.Resume === "1") {
          call.accept();
          updateActiveCall(call);
          setIncomingCall(null);
          setCallState("connected");
          setIsMuted(false);
          startTimer();
          if (custom.HeldCaller) {
            setHeldCalls((prev) => prev.filter((h) => h.caller_number !== custom.HeldCaller));
          }
          call.on("disconnect", () => handleCallEnd(call));
          call.on("cancel", () => handleCallEnd(call));
          toast.success(`Call resumed${custom.HeldCaller ? ` — ${custom.HeldCaller}` : ""}`);
          return;
        }

        setIncomingCall(call);
        // Don't clobber an in-progress call — the banner offers Hold & Answer
        if (!activeCallRef.current) setCallState("ringing");
        // Ring with the user's selected tone (location-aware)
        startRinger({ locationId: custom.locationId || "" });

        call.on("cancel", () => {
          stopRinger();
          setIncomingCall(null);
          if (!activeCallRef.current) setCallState("idle");
        });
        call.on("disconnect", () => {
          handleCallEnd(call);
        });
      });

      newDevice.on("tokenWillExpire", async () => {
        try {
          const newToken = await fetchToken();
          newDevice.updateToken(newToken);
        } catch {
          console.error("Failed to refresh voice token");
        }
      });

      deviceRef.current = newDevice;
      setDevice(newDevice);
      return newDevice;
    } catch (err) {
      if (err.response?.status === 403) {
        console.log("Call minutes exhausted");
      } else {
        console.error("Failed to create voice device:", err);
      }
      return null;
    }
  }, [fetchToken]);

  /** Register the device with Twilio (requires user gesture on Android) */
  const ensureRegistered = useCallback(async () => {
    // Already registered
    if (registeredRef.current && deviceRef.current) return deviceRef.current;

    // Create device if needed
    let dev = deviceRef.current;
    if (!dev) {
      dev = await createDevice();
      if (!dev) return null;
    }

    // Request mic permission first (critical for Android)
    const hasMic = await requestMicPermission();
    setMicPermission(hasMic);
    if (!hasMic) {
      // On Android native app, permissions might be handled natively.
      // If we're inside the native wrapper, assume permission is granted.
      if (window.FieldzNative?.isNativeApp) {
        console.log("Native app detected — bypassing web mic permission check");
        setMicPermission(true);
      } else {
        toast.error("Microphone access is required for calls. Please allow microphone access in your browser settings.");
        return null;
      }
    }

    try {
      await dev.register();
      return dev;
    } catch (err) {
      console.error("Failed to register device:", err);
      // On Android, registration can fail due to WebSocket issues.
      // Retry once with a fresh device.
      if (isAndroid() && !registeredRef.current) {
        console.log("Android: retrying registration with fresh device...");
        try {
          dev.destroy();
          deviceRef.current = null;
          dev = await createDevice();
          if (dev) {
            await dev.register();
            return dev;
          }
        } catch (retryErr) {
          console.error("Android retry failed:", retryErr);
        }
      }
      toast.error("Could not connect to phone service. Please try again.");
      return null;
    }
  }, [createDevice]);

  /** Telnyx WebRTC client — only for accounts with Telnyx (Canadian) numbers.
   *  Incoming Telnyx calls reuse the exact same banner/ringer flow. */
  const initTelnyx = useCallback(async () => {
    if (telnyxInitRef.current || !telnyxEnabledRef.current) return;
    telnyxInitRef.current = true;
    try {
      await initTelnyxClient({
        onIncoming: (shim) => {
          setIncomingCall(shim);
          if (!activeCallRef.current) setCallState("ringing");
          startRinger({});
          shim.on("cancel", () => {
            stopRinger();
            setIncomingCall(null);
            if (!activeCallRef.current) setCallState("idle");
          });
          shim.on("disconnect", () => handleCallEnd(shim));
        },
        onError: (e) => console.error("Telnyx voice error:", e?.message || e),
      });
    } catch (e) {
      telnyxInitRef.current = false;
      console.error("Telnyx client init failed:", e?.message || e);
    }
  }, []);

  // Pre-create device on mount (don't register — that waits for user gesture)
  useEffect(() => {
    createDevice().then(() => { initTelnyx(); });
    return () => {
      stopRinger();
      destroyTelnyxClient();
      telnyxInitRef.current = false;
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [createDevice, initTelnyx]);

  const startTimer = () => {
    callStartRef.current = Date.now();
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleCallEnd = useCallback((call) => {
    // A held/replaced call ending must not tear down a newer active call
    if (call && activeCallRef.current && activeCallRef.current !== call) return;
    stopRinger();
    stopTimer();
    updateActiveCall(null);
    setIncomingCall(null);
    setCallState("idle");
    setIsMuted(false);
    setCallDuration(0);
  }, []);

  const makeCall = useCallback(async (toNumber, contactInfo) => {
    if (minutesLimit !== -1 && minutesUsed >= minutesLimit) {
      toast.error("Call minute limit reached. Please upgrade your plan.");
      return;
    }

    // Telnyx (Canadian) caller ID → Telnyx WebRTC path. Twilio path unchanged.
    const fromPick = selectedCallerIdRef.current || callerId;
    const fromEntry = callerIdsRef.current.find((c) => c.phone_number === fromPick);
    if (fromEntry?.provider === "telnyx") {
      try {
        setCallState("connecting");
        const hasMic = await requestMicPermission();
        setMicPermission(hasMic);
        if (!hasMic && !window.FieldzNative?.isNativeApp) {
          toast.error("Microphone access is required for calls. Please allow microphone access in your browser settings.");
          setCallState("idle");
          return;
        }
        telnyxInitRef.current = false;
        await initTelnyx();
        const shim = telnyxNewCall(toNumber, fromPick);
        updateActiveCall(shim);
        shim.on("ringing", () => setCallState("ringing"));
        shim.on("accept", () => { setCallState("connected"); startTimer(); });
        shim.on("disconnect", () => handleCallEnd(shim));
        shim.on("cancel", () => handleCallEnd(shim));
      } catch (err) {
        console.error("Telnyx call failed:", err);
        toast.error("Failed to connect call. Please try again.");
        setCallState("idle");
      }
      return;
    }

    // Ensure device is registered (triggers mic permission on Android)
    const dev = await ensureRegistered();
    if (!dev) return;

    try {
      setCallState("connecting");

      const fromNumber = selectedCallerIdRef.current || callerId;
      const call = await dev.connect({
        params: {
          To: toNumber,
          CallerId: fromNumber,
        },
      });

      updateActiveCall(call);

      call.on("accept", () => {
        setCallState("connected");
        startTimer();
      });

      call.on("ringing", () => {
        setCallState("ringing");
      });

      call.on("disconnect", () => {
        const duration = callStartRef.current ? Math.floor((Date.now() - callStartRef.current) / 1000) : 0;
        axios.post(`${API}/voice/calls`, {
          call_sid: call.parameters?.CallSid || "",
          to_number: toNumber,
          from_number: selectedCallerIdRef.current || callerId,
          direction: "outbound",
          duration,
          status: "completed",
          customer_id: contactInfo?.id || null,
          customer_name: contactInfo?.name || null,
        }, getAuthConfig()).catch(() => {});
        handleCallEnd(call);
      });

      call.on("cancel", () => handleCallEnd(call));

      call.on("error", (err) => {
        console.error("Call error:", err.code, err.message);
        if (err.code === 31208 || err.code === 31401) {
          toast.error("Microphone blocked. Please allow microphone access and try again.");
        } else if (err.code === 31003) {
          toast.error("Network error. Check your internet connection.");
        } else {
          toast.error("Call failed. Please try again.");
        }
        handleCallEnd(call);
      });

    } catch (err) {
      console.error("Failed to make call:", err);
      if (err.name === "NotAllowedError" || err.message?.includes("permission") || err.message?.includes("Permission")) {
        // On Android native app, retry once — the native layer may handle the permission
        if (isAndroid() && window.FieldzNative?.isNativeApp) {
          console.log("Android native: retrying call after permission error...");
          try {
            const retryDev = await ensureRegistered();
            if (retryDev) {
              const retryCall = await retryDev.connect({ params: { To: toNumber, CallerId: selectedCallerIdRef.current || callerId } });
              updateActiveCall(retryCall);
              retryCall.on("accept", () => { setCallState("connected"); startTimer(); });
              retryCall.on("disconnect", () => handleCallEnd(retryCall));
              retryCall.on("cancel", () => handleCallEnd(retryCall));
              retryCall.on("error", () => handleCallEnd(retryCall));
              return;
            }
          } catch (retryErr) {
            console.error("Android retry call failed:", retryErr);
          }
        }
        toast.error("Microphone access denied. Please allow microphone in browser settings and try again.");
        setMicPermission(false);
      } else {
        toast.error("Failed to connect call. Please try again.");
      }
      setCallState("idle");
    }
  }, [callerId, minutesUsed, minutesLimit, handleCallEnd, ensureRegistered, initTelnyx]);

  const acceptIncoming = useCallback(async () => {
    if (!incomingCall) return;
    stopRinger();

    // Ensure mic permission before accepting
    const hasMic = await requestMicPermission();
    if (!hasMic) {
      toast.error("Microphone access required to answer calls.");
      return;
    }

    incomingCall.accept();
    updateActiveCall(incomingCall);
    setIncomingCall(null);
    setCallState("connected");
    startTimer();

    incomingCall.on("disconnect", () => {
      // Telnyx calls are logged server-side by the voice webhooks
      if (incomingCall.provider !== "telnyx") {
        const duration = callStartRef.current ? Math.floor((Date.now() - callStartRef.current) / 1000) : 0;
        axios.post(`${API}/voice/calls`, {
          call_sid: incomingCall.parameters?.CallSid || "",
          to_number: callerId,
          from_number: incomingCall.parameters?.From || "Unknown",
          direction: "inbound",
          duration,
          status: "completed",
        }, getAuthConfig()).catch(() => {});
      }
      handleCallEnd(incomingCall);
    });
  }, [incomingCall, callerId, handleCallEnd]);

  const rejectIncoming = useCallback(() => {
    if (incomingCall) {
      stopRinger();
      incomingCall.reject();
      setIncomingCall(null);
      setCallState("idle");
    }
  }, [incomingCall]);

  const hangUp = useCallback(() => {
    if (activeCall) activeCall.disconnect();
    if (deviceRef.current) deviceRef.current.disconnectAll();
    stopTimer();
    updateActiveCall(null);
    setCallState("idle");
    setIsMuted(false);
  }, [activeCall]);

  const toggleMute = useCallback(() => {
    if (activeCall) {
      const muted = !isMuted;
      activeCall.mute(muted);
      setIsMuted(muted);
    }
  }, [activeCall, isMuted]);

  const sendDigit = useCallback((digit) => {
    if (activeCall) activeCall.sendDigits(digit);
  }, [activeCall]);

  const selectCallerId = useCallback((num) => {
    selectedCallerIdRef.current = num;
    setSelectedCallerIdState(num);
    try { localStorage.setItem("voice_selected_caller_id", num); } catch { /* ignore */ }
  }, []);

  const holdCall = useCallback(async () => {
    const call = activeCallRef.current;
    const sid = call?.parameters?.CallSid;
    if (!call || !sid) {
      toast.error("No active call to hold");
      return false;
    }
    try {
      const res = await axios.post(`${API}/voice/hold`, { call_sid: sid }, getAuthConfig());
      setHeldCalls((prev) => [...prev.filter((h) => h.pstn_sid !== res.data.pstn_sid), res.data]);
      stopTimer();
      updateActiveCall(null);
      setCallState("idle");
      setIsMuted(false);
      setCallDuration(0);
      toast.success("Call placed on hold — the caller is hearing music");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place the call on hold");
      return false;
    }
  }, []);

  const resumeCall = useCallback(async (pstnSid) => {
    if (activeCallRef.current) {
      toast.error("Hold or end your current call before resuming another");
      return;
    }
    const dev = await ensureRegistered();
    if (!dev) return;
    try {
      const res = await axios.post(`${API}/voice/resume`, { pstn_sid: pstnSid }, getAuthConfig());
      setHeldCalls((prev) => prev.filter((h) => h.pstn_sid !== pstnSid));
      if (res.data.ended) {
        toast.info("That caller has already hung up");
      }
      // Otherwise the call reconnects as an auto-answered incoming connection (Resume=1)
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resume the call");
    }
  }, [ensureRegistered]);

  const holdAndAccept = useCallback(async () => {
    if (!incomingCall) return;
    const ok = await holdCall();
    if (!ok) return;
    acceptIncoming();
  }, [incomingCall, holdCall, acceptIncoming]);

  // Restore calls still on hold (e.g. after a page reload)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${API}/voice/held`, getAuthConfig())
      .then((res) => setHeldCalls(res.data.held || []))
      .catch(() => { /* ignore */ });
  }, []);

  const value = {
    device,
    isReady,
    activeCall,
    callState,
    incomingCall,
    isMuted,
    callDuration,
    callerId,
    minutesUsed,
    minutesLimit,
    micPermission,
    availableCallerIds,
    selectedCallerId,
    selectCallerId,
    heldCalls,
    holdCall,
    resumeCall,
    holdAndAccept,
    makeCall,
    hangUp,
    toggleMute,
    sendDigit,
    acceptIncoming,
    rejectIncoming,
    ensureRegistered,
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};

export default VoiceContext;
